import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { doc, getDoc, updateDoc, increment } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { Heart, ArrowLeft, Calendar, User, Clock } from 'lucide-react';
import { getIdFromSlug } from '../utils/slug';
import TreeDetailsLoading from '../components/TreeDetailsLoading';
import ImageWithFallback from '../components/ImageWithFallback';
import TopTrees from '../components/TopTrees';
import Footer from '../components/Footer';
import toast from 'react-hot-toast';
import SocialShare from '../components/SocialShare';
import ReportButton from '../components/ReportButton';
import ViewCounter from '../components/ViewCounter';
import ImageZoom from '../components/ImageZoom';
import BackToTop from '../components/BackToTop';
import Comments from '../components/Comments';
import ContestEndedBanner from '../components/ContestEndedBanner';
import { useContestStatus } from '../hooks/useContestStatus';
import { useVoting } from '../hooks/useVoting';
import { Tree } from '../types/tree';

export default function TreeDetails() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [tree, setTree] = useState<Tree | null>(null);
  const [loading, setLoading] = useState(true);
  const { isEnded, endedAt, loading: loadingContestStatus } = useContestStatus();
  const { 
    isVoting, 
    hasVoted, 
    timeRemaining, 
    handleVote 
  } = useVoting(tree?.id);

  useEffect(() => {
    const fetchTree = async () => {
      if (!slug) {
        navigate('/');
        return;
      }

      try {
        const treeId = getIdFromSlug(slug);
        const treeRef = doc(db, 'trees', treeId);
        const treeSnap = await getDoc(treeRef);

        if (!treeSnap.exists()) {
          toast.error('Árbol no encontrado');
          navigate('/');
          return;
        }

        const treeData = treeSnap.data();
        
        // Check if tree is rejected or deleted
        if (treeData.status === 'rejected' || treeData.status === 'deleted') {
          navigate('/');
          return;
        }

        setTree({
          id: treeSnap.id,
          ...treeData,
          createdAt: treeData.createdAt?.toDate()
        } as Tree);
      } catch (error) {
        console.error('Error fetching tree:', error);
        toast.error('Error al cargar el árbol');
        navigate('/');
      } finally {
        setLoading(false);
      }
    };

    fetchTree();
  }, [slug, navigate]);

  const formatTimeRemaining = (ms: number): string => {
    const hours = Math.floor(ms / (1000 * 60 * 60));
    const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  if (loading || loadingContestStatus) {
    return <TreeDetailsLoading />;
  }

  if (!tree) {
    return (
      <div className="min-h-screen bg-gray-50 pt-16 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Árbol no encontrado</p>
          <Link to="/" className="text-primary hover:text-primary-dark mt-4 inline-block">
            Volver al inicio
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{`${tree.name} - Concurso de Árboles de Navidad`}</title>
        <meta 
          name="description" 
          content={`Vota por el árbol de Navidad de ${tree.creator}. ${tree.votes} votos hasta ahora.`}
        />
      </Helmet>

      <div className="min-h-screen bg-gray-50 pt-16">
        <div className="container mx-auto px-4 py-8">
          <Link 
            to="/"
            className="inline-flex items-center text-primary hover:text-primary-dark mb-6"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Volver a la galería
          </Link>

          {isEnded && <ContestEndedBanner endedAt={endedAt} />}

          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="md:flex">
              <div className="md:flex-1">
                <ImageZoom src={tree.imageUrl} alt={tree.name} />
              </div>

              <div className="md:w-96 p-6 md:p-8">
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                  {tree.name}
                </h1>

                <div className="space-y-4 mb-8">
                  <div className="flex items-center text-gray-600">
                    <User className="h-5 w-5 mr-2" />
                    <span>{tree.creator}</span>
                  </div>

                  <div className="flex items-center text-gray-600">
                    <Calendar className="h-5 w-5 mr-2" />
                    <span>
                      {tree.createdAt?.toLocaleDateString('es', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </span>
                  </div>

                  <ViewCounter treeId={tree.id} />

                  <div className="flex items-center text-primary">
                    <Heart className="h-5 w-5 mr-2" />
                    <span className="font-medium">{tree.votes} votos</span>
                  </div>

                  {tree.description && (
                    <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                      <p className="text-gray-600 text-sm leading-relaxed whitespace-pre-wrap">
                        {tree.description}
                      </p>
                    </div>
                  )}
                </div>

                <div className="space-y-6">
                  <button
                    onClick={() => handleVote()}
                    disabled={isVoting || hasVoted || isEnded}
                    className={`w-full py-3 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center ${
                      hasVoted || isEnded ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : 'bg-primary hover:bg-primary-dark text-white'
                    }`}
                  >
                    <Heart className="h-5 w-5 mr-2" />
                    {isVoting ? 'Votando...' : 
                     isEnded ? 'Votación cerrada' :
                     hasVoted ? 'Ya has votado' : 
                     '¡Votar por este árbol!'}
                  </button>

                  {hasVoted && timeRemaining && !isEnded && (
                    <div className="text-center text-sm text-gray-600 flex items-center justify-center">
                      <Clock className="h-4 w-4 mr-1" />
                      <span>
                        Podrás volver a votar en {formatTimeRemaining(timeRemaining)}
                      </span>
                    </div>
                  )}

                  <a 
                    href="https://t.me/ofertium_es" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-full inline-flex items-center justify-center bg-[#0088cc] hover:bg-[#0077b5] text-white px-6 py-3 rounded-lg text-sm font-medium transition-all"
                  >
                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                    </svg>
                    Únete a Nuestro Canal de Telegram
                  </a>

                  <div className="flex justify-between items-center pt-4 border-t">
                    <SocialShare 
                      url={window.location.href}
                      title={`¡Vota por "${tree.name}" en el concurso de árboles de Navidad!`}
                      description={tree.description}
                      imageUrl={tree.imageUrl}
                    />
                    <ReportButton treeId={tree.id} treeName={tree.name} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4 py-8">
          <Comments treeId={tree.id} />
        </div>

        <TopTrees />
        <Footer />
        <BackToTop />
      </div>
    </>
  );
}